const AUTOMATIC_VOICE_ID = "auto";

export const AUDIO_VOICE_OPTIONS = Object.freeze([
  Object.freeze({ id: AUTOMATIC_VOICE_ID, label: "" }),
  Object.freeze({ id: "leda", label: "Leda" }),
  Object.freeze({ id: "aoede", label: "Aoede" }),
  Object.freeze({ id: "charon", label: "Charon" }),
  Object.freeze({ id: "orus", label: "Orus" }),
]);

const AUDIO_VOICE_IDS = new Set(AUDIO_VOICE_OPTIONS.map((voice) => voice.id));

// Mirrors `lib/tts/google-voice-for-lang.cjs`. A matrix test compares the
// complete app language catalog with the server attestation so synced Android
// preferences cannot expose a narrator the public web cannot actually use.
const NAMED_VOICE_LANGUAGE_CODES = new Set([
  "ar",
  "bn",
  "de",
  "en",
  "es",
  "fr",
  "gu",
  "hi",
  "id",
  "ja",
  "kn",
  "ml",
  "mr",
  "pa",
  "pt",
  "ru",
  "sw",
  "ta",
  "te",
  "tr",
  "ur",
  "vi",
  "yue",
  "zh",
]);

const SUPPORTED_LANGUAGE_CODES = new Set([
  ...NAMED_VOICE_LANGUAGE_CODES,
  "arz",
  "as",
  "ha",
  "or",
  "pcm",
  "pnb",
  "tl",
]);

// Mirrors `DEVICE_SPEECH_ONLY_LANGUAGE_BASES` in
// `lib/tts/google-voice-for-lang.cjs`. Automatic narration for these exact
// product languages has no same-language cloud voice and therefore requires a
// compatible voice installed on the user's device.
const DEVICE_SPEECH_ONLY_LANGUAGE_CODES = new Set(["arz", "as", "ha", "or", "pcm", "pnb"]);

const SCRIPT_BY_LANGUAGE = Object.freeze({
  ar: "arab",
  arz: "arab",
  as: "beng",
  bn: "beng",
  de: "latn",
  en: "latn",
  es: "latn",
  fr: "latn",
  gu: "gujr",
  ha: "latn",
  hi: "deva",
  id: "latn",
  ja: "jpan",
  kn: "knda",
  ml: "mlym",
  mr: "deva",
  or: "orya",
  pa: "guru",
  pcm: "latn",
  pnb: "arab",
  pt: "latn",
  ru: "cyrl",
  sw: "latn",
  ta: "taml",
  te: "telu",
  tl: "latn",
  tr: "latn",
  ur: "arab",
  vi: "latn",
  yue: "hant",
  zh: "hans",
});

function canonicalAudioLanguage(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^b\+/, "")
    .replaceAll("+", "-")
    .replaceAll("_", "-")
    .slice(0, 24);
  const parts = normalized.split("-");
  const [base = ""] = parts;
  const explicitScript = parts.find((part) => /^[a-z]{4}$/.test(part)) || "";
  const explicitRegion = parts.find((part) => /^[a-z]{2}$/.test(part) && part !== base) || "";
  if (base === "fil") return !explicitScript || explicitScript === "latn" ? "tl" : null;
  if (base === "lah") return !explicitScript || explicitScript === "arab" ? "pnb" : null;
  if (/^pa-(?:arab(?:-|$)|pk(?:-|$))/.test(normalized)) return "pnb";
  if (/^zh-(?:hk(?:-|$)|hant-hk(?:-|$))/.test(normalized)) {
    return "yue";
  }
  if (base === "zh" && (explicitScript === "hant" || ["tw", "mo"].includes(explicitRegion))) {
    return null;
  }
  if (SUPPORTED_LANGUAGE_CODES.has(normalized)) return normalized;
  if (!SUPPORTED_LANGUAGE_CODES.has(base)) return null;
  if (explicitScript && SCRIPT_BY_LANGUAGE[base] !== explicitScript) return null;
  return base;
}

export function normalizedAudioVoiceId(value) {
  const id = String(value || "")
    .trim()
    .toLowerCase();
  return AUDIO_VOICE_IDS.has(id) ? id : AUTOMATIC_VOICE_ID;
}

export function namedAudioVoicesSupportedForLanguage(languageCode) {
  return NAMED_VOICE_LANGUAGE_CODES.has(canonicalAudioLanguage(languageCode));
}

export function deviceSpeechOnlyForLanguage(languageCode) {
  return DEVICE_SPEECH_ONLY_LANGUAGE_CODES.has(canonicalAudioLanguage(languageCode));
}

export function audioVoiceIdSupportedForLanguage(voiceId, languageCode) {
  const normalized = normalizedAudioVoiceId(voiceId);
  return normalized === AUTOMATIC_VOICE_ID || namedAudioVoicesSupportedForLanguage(languageCode);
}

export function compatibleAudioVoiceId(voiceId, languageCode) {
  const normalized = normalizedAudioVoiceId(voiceId);
  return audioVoiceIdSupportedForLanguage(normalized, languageCode)
    ? normalized
    : AUTOMATIC_VOICE_ID;
}

export function assertAudioVoiceIdSupportedForLanguage(voiceId, languageCode) {
  if (audioVoiceIdSupportedForLanguage(voiceId, languageCode)) return;
  const error = new Error("The selected narrator is unavailable for this language.");
  error.code = "audio_voice_unsupported_for_language";
  error.status = 422;
  throw error;
}

export function audioVoiceOptionsForLanguage(languageCode) {
  return namedAudioVoicesSupportedForLanguage(languageCode)
    ? AUDIO_VOICE_OPTIONS
    : AUDIO_VOICE_OPTIONS.slice(0, 1);
}
